import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './areas-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import usePersistedColumns from '../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';
import AreasTable, { COLUMN_DEFS, getAreaFeatures } from './components/AreasTable.jsx';
import AreaGroupsTable, { GROUP_COLUMN_DEFS } from './components/AreaGroupsTable.jsx';
import BuildingsTable, { BUILDING_COLUMN_DEFS } from './components/BuildingsTable.jsx';
import AreaDrawer from './components/AreaDrawer.jsx';
import BulkAreaGroupDrawer from './components/BulkAreaGroupDrawer.jsx';
import BulkAreaTypeDrawer from './components/BulkAreaTypeDrawer.jsx';
import AreaGroupDrawer from './components/AreaGroupDrawer.jsx';
import BuildingDrawer from './components/BuildingDrawer.jsx';
import AreaDeviceDrawer from './components/AreaDeviceDrawer.jsx';
import EasyAddDeviceDrawer from '../../../components/v2/EasyAddDeviceDrawer.jsx';
import ConfirmTypedDialog from '../../../components/v2/ConfirmTypedDialog.jsx';
// Shared components from residents (fully generic)
import ActiveFilterBar from '../residents/components/ActiveFilterBar.jsx';
import ColumnVisibilityToggle from '../residents/components/ColumnVisibilityToggle.jsx';
import TableSkeleton from '../residents/components/TableSkeleton.jsx';
// V2 data layer
import { getSession, getCampusId, getUserId, getSessionId, getActiveVendorIdList, getCampusList, updateSessionCampus, getRoleWeight, getCampusConfig } from '../../../services/session.js';
import { iAmHigherAuthority } from '../../../libs.jsx';
import { fetchAreas, fetchAreaTypes, fetchDevices, addArea, addBulkAreas, editArea, deleteAreas, assignDeviceToArea, unassignDevicesFromArea, fetchExternalAreaData, bulkEditAreaGroups, bulkEditAreaTypes } from './api/areasApi.js';
import { fetchSpeakerDevices, addDevice } from '../../devices/api/devicesApi.js';
import { fetchAreaGroups, addAreaGroup, editAreaGroup, deleteAreaGroups } from './api/areaGroupsApi.js';
import { fetchBuildings, addBuilding, editBuilding, deleteBuildings } from './api/buildingsApi.js';
import { exportAreasCsv, exportAreaGroupsCsv, exportBuildingsCsv } from '../../../services/csvExport.js';

/** All column keys per tab */
const ALL_AREA_KEYS = COLUMN_DEFS.map((c) => c.key);
const ALL_GROUP_KEYS = GROUP_COLUMN_DEFS.map((c) => c.key);
const ALL_BUILDING_KEYS = BUILDING_COLUMN_DEFS.map((c) => c.key);

// Features column detection. Room Calling requires a Grandstream Speaker
// (manufacture_id 10, device_type_id 2) on the area; Fall Protection is keyed
// off the SafelyYou vendor via the external-area mapping.
const SPEAKER_MANUFACTURE_ID = 10;
const SPEAKER_DEVICE_TYPE_ID = 2;
const SAFELYYOU_VENDOR_ID = 17;

/** Default visible columns — show all by default (user can hide via column toggle) */
const DEFAULT_AREA_VISIBLE = ALL_AREA_KEYS;
const DEFAULT_GROUP_VISIBLE = ALL_GROUP_KEYS;
const DEFAULT_BUILDING_VISIBLE = ALL_BUILDING_KEYS;

/**
 * AreasPage — main orchestrator for the /community/areas V2 redesign.
 *
 * Manages: real API data loading for Areas, Area Groups, and Buildings tabs,
 * search, column filters, sort, pagination, row selection, drawers, and tab switching.
 */
const AreasPage = ({ initialTab = 'areas' }) => {
  // ---------- Session ----------
  const sessionCampusId = getCampusId();
  const campusList = useMemo(() => getCampusList() || [], []);
  const myRoleWeight = getRoleWeight();
  // Areas tab: add/edit/delete an area requires role_weight > 20.
  const canModifyAreas = iAmHigherAuthority(20, myRoleWeight);
  // Area Groups tab: add/delete groups + edit name/description requires > 80.
  // Below that, users can still open Edit to change area assignments only.
  const canManageGroups = iAmHigherAuthority(80, myRoleWeight);
  // Buildings tab: add/edit/delete (including area assignments) requires > 20.
  const canModifyBuildings = iAmHigherAuthority(20, myRoleWeight);
  // System (manufacture_id=1, device_category_id IN (4,5)) and SIP (manufacture_id=10)
  // devices remain visible everywhere; only > 80 may add/remove them.
  const canManageRestrictedDevices = iAmHigherAuthority(80, myRoleWeight);
  // System device types that don't have a per-device profile page and
  // shouldn't be managed by non-admins (repeaters, etc.). We check by
  // device_type_id in addition to device_category_id because some
  // stored procedures (e.g. getareadevices, used on AreaProfilePage)
  // don't reliably return device_category_id — without this second
  // check, restricted devices would slip through when cid is absent.
  const SYSTEM_RESTRICTED_TYPE_IDS = useMemo(() => new Set([10, 11, 56, 57]), []);
  const isRestrictedDevice = useCallback((d) => {
    const mid = parseInt(d?.manufacture_id, 10);
    const cid = parseInt(d?.device_category_id, 10);
    const tid = parseInt(d?.device_type_id, 10);
    if (mid === 10) return true;
    if (mid === 1 && (cid === 4 || cid === 5)) return true;
    if (mid === 1 && SYSTEM_RESTRICTED_TYPE_IDS.has(tid)) return true;
    return false;
  }, [SYSTEM_RESTRICTED_TYPE_IDS]);

  // ---------- Data & Loading ----------
  const [areasData, setAreasData] = useState([]);
  const [areaTypes, setAreaTypes] = useState([]);
  const [devicesData, setDevicesData] = useState([]);
  // Features column — area_ids with each capability (see the Features constants).
  const [roomCallingAreaIds, setRoomCallingAreaIds] = useState(() => new Set());
  const [fallProtectionAreaIds, setFallProtectionAreaIds] = useState(() => new Set());
  const [areaGroupsData, setAreaGroupsData] = useState([]);
  const [buildingsData, setBuildingsData] = useState([]);
  const [areasLoading, setAreasLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [buildingsLoading, setBuildingsLoading] = useState(true);
  const [campusId, setCampusId] = useState(sessionCampusId || 0);
  const firstLoad = useRef(true);

  // Grandstream-only: speakers come from a separate stored proc (getsipdevices)
  // and are merged into the device pool below so the AreaDeviceDrawer can
  // assign them like any other device.
  const isGrandstream = useMemo(() => Boolean(getCampusConfig()?.is_grandstream), [campusId]);

  // ---------- Tab ----------
  const activeTab = initialTab;

  // ---------- Page-level Toast ----------
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // ==========================================================================
  // AREAS TAB STATE
  // ==========================================================================
  const [areaSearch, setAreaSearch] = useState('');
  const [areaFilters, setAreaFilters] = useState({});
  const [areaSortColumn, setAreaSortColumn] = useState('area_name');
  const [areaSortOrder, setAreaSortOrder] = useState('asc');
  const [areaPage, setAreaPage] = useState(1);
  const [areaPerPage, setAreaPerPage] = usePersistedPerPage();
  const [areaVisibleColumns, handleAreaColumnToggle, resetAreaColumns] = usePersistedColumns('areas', DEFAULT_AREA_VISIBLE, ALL_AREA_KEYS, ['features']);
  const [areaSelectedIds, setAreaSelectedIds] = useState([]);
  const [areaDrawerOpen, setAreaDrawerOpen] = useState(false);
  // Bulk area-group edit drawer
  const [bulkAreaGroupDrawerOpen, setBulkAreaGroupDrawerOpen] = useState(false);
  const [bulkAreaGroupSaving, setBulkAreaGroupSaving] = useState(false);
  // Bulk area-type edit drawer
  const [bulkAreaTypeDrawerOpen, setBulkAreaTypeDrawerOpen] = useState(false);
  const [bulkAreaTypeSaving, setBulkAreaTypeSaving] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  /* Typed confirmations for the destructive paths. Each row state doubles as the
     dialog's open flag. Matches ResidentsPage — see ConfirmTypedDialog. */
  const [deleteAreaRow, setDeleteAreaRow] = useState(null);
  const [deleteAreaSaving, setDeleteAreaSaving] = useState(false);
  const [bulkDeleteAreasOpen, setBulkDeleteAreasOpen] = useState(false);
  const [bulkDeleteAreasSaving, setBulkDeleteAreasSaving] = useState(false);
  const [deviceDrawerOpen, setDeviceDrawerOpen] = useState(false);
  const [deviceArea, setDeviceArea] = useState(null);
  // Quick add-and-assign, launched from inside the manage-devices drawer
  const [easyAddOpen, setEasyAddOpen] = useState(false);

  // ==========================================================================
  // AREA GROUPS TAB STATE
  // ==========================================================================
  const [groupSearch, setGroupSearch] = useState('');
  const [groupFilters, setGroupFilters] = useState({});
  const [groupSortColumn, setGroupSortColumn] = useState('area_group_name');
  const [groupSortOrder, setGroupSortOrder] = useState('asc');
  const [groupPage, setGroupPage] = useState(1);
  const [groupPerPage, setGroupPerPage] = usePersistedPerPage();
  const [groupVisibleColumns, handleGroupColumnToggle, resetGroupColumns] = usePersistedColumns('area-groups', DEFAULT_GROUP_VISIBLE, ALL_GROUP_KEYS);
  const [groupSelectedIds, setGroupSelectedIds] = useState([]);
  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteGroupRow, setDeleteGroupRow] = useState(null);
  const [deleteGroupSaving, setDeleteGroupSaving] = useState(false);
  const [groupBulkDeleteOpen, setGroupBulkDeleteOpen] = useState(false);
  const [groupBulkDeleteSaving, setGroupBulkDeleteSaving] = useState(false);

  // ==========================================================================
  // BUILDINGS TAB STATE
  // ==========================================================================
  const [buildingSearch, setBuildingSearch] = useState('');
  const [buildingFilters, setBuildingFilters] = useState({});
  const [buildingSortColumn, setBuildingSortColumn] = useState('building_name');
  const [buildingSortOrder, setBuildingSortOrder] = useState('asc');
  const [buildingPage, setBuildingPage] = useState(1);
  const [buildingPerPage, setBuildingPerPage] = usePersistedPerPage();
  const [buildingVisibleColumns, handleBuildingColumnToggle, resetBuildingColumns] = usePersistedColumns('buildings', DEFAULT_BUILDING_VISIBLE, ALL_BUILDING_KEYS);
  const [buildingSelectedIds, setBuildingSelectedIds] = useState([]);
  const [buildingDrawerOpen, setBuildingDrawerOpen] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [deleteBuildingRow, setDeleteBuildingRow] = useState(null);
  const [deleteBuildingSaving, setDeleteBuildingSaving] = useState(false);
  const [buildingBulkDeleteOpen, setBuildingBulkDeleteOpen] = useState(false);
  const [buildingBulkDeleteSaving, setBuildingBulkDeleteSaving] = useState(false);

  // ---------- Helper: get session params for API write calls ----------
  const getSessionParams = useCallback(() => {
    return {
      campusId,
      userId: getUserId(),
      clientId: getSessionId(),
      vendor_ids: getActiveVendorIdList(),
    };
  }, [campusId]);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================

  /** Load areas + area types + devices */
  const loadAreas = useCallback(async () => {
    setAreasLoading(true);
    try {
      const isFirstLoad = firstLoad.current;
      // Fetch inovonics/standard devices, and speakers in parallel. On
      // non-grandstream campuses fetchSpeakerDevices is skipped — the
      // procedure returns nothing useful there and we avoid a wasted RTT.
      const [aData, atData, dData, sData] = await Promise.all([
        fetchAreas(campusId, !isFirstLoad),
        fetchAreaTypes(),
        fetchDevices(campusId),
        isGrandstream ? fetchSpeakerDevices(campusId, !isFirstLoad) : Promise.resolve([]),
      ]);
      firstLoad.current = false;
      setAreasData(aData || []);
      setAreaTypes(atData || []);
      // Merge speaker devices into the available-device pool. Dedupe by
      // device_id defensively in case a future query overlap snuck a
      // speaker into both procs.
      const combined = [...(dData || [])];
      const seen = new Set(combined.map((d) => d.device_id));
      (sData || []).forEach((d) => {
        if (!seen.has(d.device_id)) {
          combined.push(d);
          seen.add(d.device_id);
        }
      });
      setDevicesData(combined);

      // Room Calling feature: area_ids that have a Grandstream Speaker attached.
      // Speakers carry their assignment in area_id (mirrors AreaProfilePage).
      const speakerAreas = new Set();
      (sData || []).forEach((d) => {
        if (
          parseInt(d?.manufacture_id, 10) === SPEAKER_MANUFACTURE_ID &&
          parseInt(d?.device_type_id, 10) === SPEAKER_DEVICE_TYPE_ID &&
          d?.area_id != null
        ) {
          speakerAreas.add(Number(d.area_id));
        }
      });
      setRoomCallingAreaIds(speakerAreas);
    } catch (err) {
      console.error('[AreasPage] Failed to load areas data:', err);
    } finally {
      setAreasLoading(false);
    }
  }, [campusId, isGrandstream]);

  /** Load area groups */
  const loadAreaGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const data = await fetchAreaGroups(campusId);
      setAreaGroupsData(data || []);
    } catch (err) {
      console.error('[AreasPage] Failed to load area groups:', err);
    } finally {
      setGroupsLoading(false);
    }
  }, [campusId]);

  /** Load buildings */
  const loadBuildings = useCallback(async () => {
    setBuildingsLoading(true);
    try {
      const data = await fetchBuildings(campusId);
      setBuildingsData(data || []);
    } catch (err) {
      console.error('[AreasPage] Failed to load buildings:', err);
    } finally {
      setBuildingsLoading(false);
    }
  }, [campusId]);

  // Initial load
  useEffect(() => { loadAreas(); }, [loadAreas]);
  useEffect(() => { loadAreaGroups(); }, [loadAreaGroups]);
  useEffect(() => { loadBuildings(); }, [loadBuildings]);

  // Fall Protection feature: one external-area lookup for all active vendors
  // (empty area_ids = all areas). Rows are attributed by vendor_id; SafelyYou
  // (vendor 17) marks an area as having fall protection. Non-fatal on failure.
  useEffect(() => {
    let cancelled = false;
    const activeVendors = getActiveVendorIdList(campusId).map(Number).filter((v) => !Number.isNaN(v));
    if (!activeVendors.length) {
      setFallProtectionAreaIds(new Set());
      return;
    }
    (async () => {
      try {
        const result = await fetchExternalAreaData(campusId, '', activeVendors.join(','));
        if (cancelled) return;
        const set = new Set();
        (Array.isArray(result) ? result : []).forEach((r) => {
          if (Number(r?.vendor_id) === SAFELYYOU_VENDOR_ID && r?.area_id != null) {
            set.add(Number(r.area_id));
          }
        });
        setFallProtectionAreaIds(set);
      } catch (err) {
        console.error('[AreasPage] External-area (features) lookup failed:', err);
        if (!cancelled) setFallProtectionAreaIds(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [campusId]);

  // ==========================================================================
  // AREAS TAB — Filter / Search / Sort pipeline
  // ==========================================================================
  const filteredAreas = useMemo(() => {
    let data = [...areasData];

    // 1. Search
    if (areaSearch.trim()) {
      const s = areaSearch.toLowerCase();
      data = data.filter(
        (r) =>
          r.area_name?.toLowerCase().includes(s) ||
          r.area_type_description?.toLowerCase().includes(s) ||
          r.building_name?.toLowerCase().includes(s) ||
          (r.area_groups || []).some((g) => (g.area_group_name || '').toLowerCase().includes(s)) ||
          (r.devices || []).some((d) => (d.device_name || d.description || d.descriptions || '').toLowerCase().includes(s)) ||
          (r.residents || []).some((res) =>
            res.resident_first_name?.toLowerCase().includes(s) ||
            res.resident_last_name?.toLowerCase().includes(s) ||
            (res.resident_name || `${res.resident_first_name || ''} ${res.resident_last_name || ''}`.trim()).toLowerCase().includes(s)
          )
      );
    }

    // 2. Column filters
    Object.entries(areaFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;

      data = data.filter((row) => {
        if (columnKey === 'area_group_name') {
          const groups = row.area_groups || [];
          if (values.includes('(empty)') && groups.length === 0) return true;
          return groups.some((g) => values.includes(g.area_group_name));
        }
        if (columnKey === 'devices') {
          const devs = row.devices || [];
          if (values.includes('(empty)') && devs.length === 0) return true;
          return devs.some((d) => values.includes(d.device_name || d.description || d.descriptions));
        }
        if (columnKey === 'features') {
          const labels = getAreaFeatures(row, { isGrandstream, roomCallingAreaIds, fallProtectionAreaIds }).map((f) => f.label);
          if (values.includes('(empty)') && labels.length === 0) return true;
          return labels.some((l) => values.includes(l));
        }
        const val = row[columnKey];
        const strVal = val != null && val !== '' ? String(val) : '(empty)';
        return values.includes(strVal);
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal = a[areaSortColumn];
      let bVal = b[areaSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return areaSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [areasData, areaSearch, areaFilters, areaSortColumn, areaSortOrder, isGrandstream, roomCallingAreaIds, fallProtectionAreaIds]);

  // Areas Pagination
  const areaTotalFiltered = filteredAreas.length;
  const areaTotalPages = Math.max(1, Math.ceil(areaTotalFiltered / areaPerPage));
  const areaSafePage = Math.min(areaPage, areaTotalPages);

  const paginatedAreas = useMemo(() => {
    const start = (areaSafePage - 1) * areaPerPage;
    return filteredAreas.slice(start, start + areaPerPage);
  }, [filteredAreas, areaSafePage, areaPerPage]);

  const areaShowingStart = areaTotalFiltered === 0 ? 0 : (areaSafePage - 1) * areaPerPage + 1;
  const areaShowingEnd = Math.min(areaSafePage * areaPerPage, areaTotalFiltered);

  useEffect(() => { setAreaPage(1); }, [areaSearch, areaFilters, areaPerPage]);

  const areaPageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, areaSafePage - Math.floor(maxButtons / 2));
    let end = Math.min(areaTotalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [areaSafePage, areaTotalPages]);

  // Areas handlers
  const handleAreaSort = useCallback((column) => {
    if (areaSortColumn === column) {
      setAreaSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setAreaSortColumn(column);
      setAreaSortOrder('asc');
    }
  }, [areaSortColumn]);

  const handleAreaFilterChange = useCallback((columnKey, values) => {
    setAreaFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleAreaRemoveFilter = useCallback((columnKey, value) => {
    setAreaFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleAreaClearAllFilters = useCallback(() => {
    setAreaFilters({});
    setAreaSearch('');
  }, []);

  const handleAreaSelectRow = useCallback((id) => {
    setAreaSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleAreaSelectAll = useCallback(() => {
    const allIds = paginatedAreas.map((r) => r.area_id);
    const allSelected = allIds.every((id) => areaSelectedIds.includes(id));
    if (allSelected) {
      setAreaSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setAreaSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [paginatedAreas, areaSelectedIds]);

  // Area drawer handlers
  const openAddArea = useCallback(() => {
    if (!canModifyAreas) {
      showToast('error', 'You do not have permission to add areas.');
      return;
    }
    setEditingArea(null);
    setAreaDrawerOpen(true);
  }, [canModifyAreas, showToast]);

  // Open is allowed for all roles — drawer disables non-area-group fields
  // when the user lacks canModifyAreas.
  const openEditArea = useCallback((area) => {
    setEditingArea(area);
    setAreaDrawerOpen(true);
  }, []);

  const openDeviceDrawer = useCallback((area) => {
    setDeviceArea(area);
    setDeviceDrawerOpen(true);
  }, []);

  const handleSaveArea = useCallback(
    async (formData) => {
      const session = getSessionParams();
      let result;

      if (formData._bulk) {
        // Bulk add
        result = await addBulkAreas(formData, session);
      } else if (formData.area_id) {
        // Edit mode
        const original = areasData.find((a) => a.area_id === formData.area_id);
        const oldGroupIds = (original?.area_groups || []).map((g) => g.area_group_id);
        const oldData = {
          ...original,
          area_group_ids: oldGroupIds,
        };
        result = await editArea(formData, oldData, session);
      } else {
        // Single add
        result = await addArea(formData, session);
      }

      if (result[0]) {
        setAreaDrawerOpen(false);
        loadAreas();
        loadAreaGroups();
        loadBuildings();
        showToast('success', formData._bulk
          ? `${formData.baseName}${formData.startNumber}–${formData.baseName}${formData.endNumber} added`
          : formData.area_id
            ? `${formData.area_name} updated`
            : `${formData.area_name} added`
        );
      } else {
        showToast('error', result[1] || 'Failed to save area');
      }
    },
    [areasData, getSessionParams, loadAreas, loadAreaGroups, loadBuildings, showToast]
  );

  // Data-side block reason only (role gating is enforced via visibility).
  // Returns null when nothing blocks deletion at the data level.
  const getDeleteAreaBlockedReason = useCallback((area) => {
    const residentCount = (area?.residents || []).length;
    const deviceCount = (area?.devices || []).length;
    if (residentCount > 0 || deviceCount > 0) {
      return 'All devices and residents must be removed in order to delete the area';
    }
    return null;
  }, []);

  /* Entry point for the table kebab and the area drawer's Delete button. The
     permission and blocked-reason checks stay here so a refused delete still
     answers with a toast rather than opening a dialog that cannot succeed. */
  const handleDeleteArea = useCallback(
    (area) => {
      if (!area) return;
      if (!canModifyAreas) {
        showToast('error', 'You do not have permission to delete areas.');
        return;
      }
      const reason = getDeleteAreaBlockedReason(area);
      if (reason) {
        showToast('error', reason);
        return;
      }
      setDeleteAreaRow(area);
    },
    [canModifyAreas, getDeleteAreaBlockedReason, showToast]
  );

  const handleConfirmDeleteArea = useCallback(async () => {
    const area = deleteAreaRow;
    if (!area) return;
    /* Re-check on confirm: the dialog can outlive a refresh that moved a
       resident or device into this area. */
    const reason = getDeleteAreaBlockedReason(area);
    if (reason) {
      setDeleteAreaRow(null);
      showToast('error', reason);
      return;
    }
    setDeleteAreaSaving(true);
    const session = getSessionParams();
    const areaName = area.area_name;
    const result = await deleteAreas([area], session);
    setDeleteAreaSaving(false);
    if (result[0]) {
      setDeleteAreaRow(null);
      setAreaSelectedIds((prev) => prev.filter((id) => id !== area.area_id));
      setAreaDrawerOpen(false);
      loadAreas();
      loadAreaGroups();
      loadBuildings();
      showToast('success', `${areaName} deleted`);
    } else {
      showToast('error', result[1] || 'Delete failed');
    }
  }, [deleteAreaRow, getDeleteAreaBlockedReason, getSessionParams, loadAreas, loadAreaGroups, loadBuildings, showToast]);

  // Bulk area-type edit — the drawer only hands back areas whose type differs
  // from the chosen one.
  const handleBulkEditAreaTypes = useCallback(async (changes) => {
    if (!canModifyAreas) {
      showToast('error', 'You do not have permission to modify areas.');
      return;
    }
    if (!changes || changes.length === 0) {
      setBulkAreaTypeDrawerOpen(false);
      return;
    }
    setBulkAreaTypeSaving(true);
    const session = getSessionParams();
    const result = await bulkEditAreaTypes(changes, session);
    setBulkAreaTypeSaving(false);

    if (result[0]) {
      setBulkAreaTypeDrawerOpen(false);
      setAreaSelectedIds([]);
      loadAreas();
      showToast('success', `Area type updated for ${changes.length} area${changes.length !== 1 ? 's' : ''}`);
    } else {
      showToast('error', result[1] || 'Failed to update area type');
    }
  }, [canModifyAreas, getSessionParams, loadAreas, showToast]);

  // Bulk area-group edit — the drawer resolves the checked set into a full group
  // list per area and only hands back areas that actually change.
  const handleBulkEditAreaGroups = useCallback(async (changes) => {
    if (!canModifyAreas) {
      showToast('error', 'You do not have permission to modify areas.');
      return;
    }
    if (!changes || changes.length === 0) {
      setBulkAreaGroupDrawerOpen(false);
      return;
    }
    setBulkAreaGroupSaving(true);
    const session = getSessionParams();
    const result = await bulkEditAreaGroups(changes, session);
    setBulkAreaGroupSaving(false);

    if (result[0]) {
      setBulkAreaGroupDrawerOpen(false);
      setAreaSelectedIds([]);
      loadAreas();
      loadAreaGroups();
      showToast('success', `Groups updated for ${changes.length} area${changes.length !== 1 ? 's' : ''}`);
    } else {
      showToast('error', result[1] || 'Failed to update area groups');
    }
  }, [canModifyAreas, getSessionParams, loadAreas, loadAreaGroups, showToast]);

  /* Which of the selected areas can actually go. Areas still holding residents
     or devices are skipped rather than failing the batch, so the dialog has to
     say so up front — the old native confirm did, and dropping it would make the
     success toast's "(2 skipped)" arrive out of nowhere. Shared by the dialog
     body and the confirm handler so the two can never disagree. */
  const bulkAreaDeletePlan = useMemo(() => {
    const selected = areaSelectedIds
      .map((id) => areasData.find((a) => a.area_id === id))
      .filter(Boolean);
    const deletable = selected.filter((a) => !getDeleteAreaBlockedReason(a));
    return { selected, deletable, blocked: selected.length - deletable.length };
  }, [areaSelectedIds, areasData, getDeleteAreaBlockedReason]);

  const handleBulkDeleteAreas = useCallback(() => {
    if (!canModifyAreas) {
      showToast('error', 'You do not have permission to delete areas.');
      return;
    }
    if (bulkAreaDeletePlan.deletable.length === 0) {
      showToast('error', 'No selected areas can be deleted — all have residents or devices assigned.');
      return;
    }
    setBulkDeleteAreasOpen(true);
  }, [canModifyAreas, bulkAreaDeletePlan, showToast]);

  const handleConfirmBulkDeleteAreas = useCallback(async () => {
    const { deletable, blocked } = bulkAreaDeletePlan;
    /* The plan recomputes from live data, so a refresh while the dialog is open
       can move a resident or device into the last deletable area. Say so instead
       of silently doing nothing. */
    if (deletable.length === 0) {
      setBulkDeleteAreasOpen(false);
      showToast('error', 'No selected areas can be deleted — all have residents or devices assigned.');
      return;
    }
    setBulkDeleteAreasSaving(true);
    const deletableIds = deletable.map((a) => a.area_id);
    const session = getSessionParams();
    const result = await deleteAreas(deletable, session);
    setBulkDeleteAreasSaving(false);
    if (result[0]) {
      setBulkDeleteAreasOpen(false);
      setAreaSelectedIds((prev) => prev.filter((id) => !deletableIds.includes(id)));
      loadAreas();
      loadAreaGroups();
      loadBuildings();
      const skippedSuffix = blocked > 0 ? ` (${blocked} skipped)` : '';
      showToast('success', `${deletableIds.length} area${deletableIds.length > 1 ? 's' : ''} deleted${skippedSuffix}`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [bulkAreaDeletePlan, getSessionParams, loadAreas, loadAreaGroups, loadBuildings, showToast]);

  // Device drawer handlers
  const handleAssignDeviceToArea = useCallback(
    async (areaId, areaName, device, descriptions) => {
      const session = getSessionParams();
      const result = await assignDeviceToArea(device, areaId, areaName, session, isGrandstream, descriptions);
      if (result[0]) {
        await loadAreas();
      }
      return result;
    },
    [getSessionParams, loadAreas]
  );

  const handleRemoveDeviceFromArea = useCallback(
    async (areaId, devices) => {
      const session = getSessionParams();
      const result = await unassignDevicesFromArea(devices, areaId, session, isGrandstream);
      if (result[0]) {
        await loadAreas();
      }
      return result;
    },
    [getSessionParams, loadAreas]
  );

  // Quick add: creates the device and assigns it to the area in one call.
  // loadAreas() also refreshes devicesData, so the new device shows up in the
  // manage-devices drawer behind this one without a second fetch.
  const handleEasyAddAreaDevice = useCallback(async (formData) => {
    const session = getSessionParams();
    const result = await addDevice(formData, 'device_add_area', session);
    if (result[0]) {
      await loadAreas();
      showToast('success', `${formData.device_name || 'Device'} added and assigned`);
    }
    return result;
  }, [getSessionParams, loadAreas, showToast]);

  // Duplicate serial in the easy flow — assign the existing device instead of
  // erroring, so the user never needs to know whether it was already added.
  const handleEasyAssignExistingToArea = useCallback(async (device) => {
    if (!deviceArea) return [false, 'No area selected'];
    const result = await handleAssignDeviceToArea(deviceArea.area_id, deviceArea.area_name, device);
    if (result[0]) {
      showToast('success', `${device.device_name || 'Device'} assigned to ${deviceArea.area_name}`);
    }
    return result;
  }, [deviceArea, handleAssignDeviceToArea, showToast]);

  // Keep deviceArea in sync when areasData refreshes
  useEffect(() => {
    if (deviceArea && deviceDrawerOpen) {
      const updated = areasData.find((a) => a.area_id === deviceArea.area_id);
      if (updated && updated !== deviceArea) {
        setDeviceArea(updated);
      }
    }
  }, [areasData, deviceArea, deviceDrawerOpen]);

  const deviceDrawerAssigned = deviceArea?.devices || [];

  // Area export
  const handleAreaExportAll = useCallback(() => {
    if (filteredAreas.length) exportAreasCsv(filteredAreas, 'areas.csv', myRoleWeight, areaVisibleColumns);
  }, [filteredAreas, myRoleWeight, areaVisibleColumns]);

  const handleAreaBulkExport = useCallback(() => {
    const selected = areasData.filter((a) => areaSelectedIds.includes(a.area_id));
    if (selected.length) exportAreasCsv(selected, 'areas-selected.csv', myRoleWeight, areaVisibleColumns);
  }, [areaSelectedIds, areasData, myRoleWeight, areaVisibleColumns]);

  // ==========================================================================
  // AREA GROUPS TAB — Filter / Search / Sort pipeline
  // ==========================================================================
  const enrichedGroups = useMemo(() => {
    return (areaGroupsData || []).map((group) => ({
      ...group,
      area_count: (group.areas || []).length,
    }));
  }, [areaGroupsData]);

  /* Blast-radius counts for the delete confirmations. Read from the live data
     rather than the captured row so the number reflects the latest load, and
     count areas distinctly for the bulk case — one area can sit in several of
     the selected groups. A count of 0 just omits the sentence. */
  const deleteAreaRowGroupCount = useMemo(() => {
    if (!deleteAreaRow) return 0;
    const fresh = areasData.find((a) => a.area_id === deleteAreaRow.area_id);
    return ((fresh || deleteAreaRow).area_groups || []).length;
  }, [deleteAreaRow, areasData]);

  const deleteGroupAreaCount = useMemo(() => {
    if (!deleteGroupRow) return 0;
    const fresh = (areaGroupsData || []).find(
      (g) => g.area_group_id === deleteGroupRow.area_group_id
    );
    return ((fresh || deleteGroupRow).areas || []).length;
  }, [deleteGroupRow, areaGroupsData]);

  const groupBulkAreaCount = useMemo(() => {
    if (!groupBulkDeleteOpen) return 0;
    const ids = new Set();
    (areaGroupsData || []).forEach((g) => {
      if (!groupSelectedIds.includes(g.area_group_id)) return;
      (g.areas || []).forEach((a) => ids.add(a.area_id));
    });
    return ids.size;
  }, [groupBulkDeleteOpen, groupSelectedIds, areaGroupsData]);

  const filteredGroups = useMemo(() => {
    let data = [...enrichedGroups];

    // 1. Search
    if (groupSearch.trim()) {
      const s = groupSearch.toLowerCase();
      data = data.filter(
        (g) =>
          g.area_group_name?.toLowerCase().includes(s) ||
          g.area_group_description?.toLowerCase().includes(s) ||
          (g.areas || []).some((a) => a.area_name?.toLowerCase().includes(s))
      );
    }

    // 2. Column filters
    Object.entries(groupFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      data = data.filter((row) => {
        const val = row[columnKey];
        const strVal = val != null && val !== '' ? String(val) : '(empty)';
        return values.includes(strVal);
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal, bVal;
      if (groupSortColumn === 'area_count') {
        aVal = (a.areas || []).length;
        bVal = (b.areas || []).length;
        const result = aVal - bVal;
        return groupSortOrder === 'asc' ? result : -result;
      }
      aVal = a[groupSortColumn];
      bVal = b[groupSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return groupSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [enrichedGroups, groupSearch, groupFilters, groupSortColumn, groupSortOrder]);

  // Groups Pagination
  const groupTotalFiltered = filteredGroups.length;
  const groupTotalPages = Math.max(1, Math.ceil(groupTotalFiltered / groupPerPage));
  const groupSafePage = Math.min(groupPage, groupTotalPages);

  const paginatedGroups = useMemo(() => {
    const start = (groupSafePage - 1) * groupPerPage;
    return filteredGroups.slice(start, start + groupPerPage);
  }, [filteredGroups, groupSafePage, groupPerPage]);

  const groupShowingStart = groupTotalFiltered === 0 ? 0 : (groupSafePage - 1) * groupPerPage + 1;
  const groupShowingEnd = Math.min(groupSafePage * groupPerPage, groupTotalFiltered);

  useEffect(() => { setGroupPage(1); }, [groupSearch, groupFilters, groupPerPage]);

  const groupPageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, groupSafePage - Math.floor(maxButtons / 2));
    let end = Math.min(groupTotalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [groupSafePage, groupTotalPages]);

  // Group handlers
  const handleGroupSort = useCallback((column) => {
    if (groupSortColumn === column) {
      setGroupSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setGroupSortColumn(column);
      setGroupSortOrder('asc');
    }
  }, [groupSortColumn]);

  const handleGroupFilterChange = useCallback((columnKey, values) => {
    setGroupFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleGroupRemoveFilter = useCallback((columnKey, value) => {
    setGroupFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleGroupClearAllFilters = useCallback(() => {
    setGroupFilters({});
    setGroupSearch('');
  }, []);

  const handleGroupSelectRow = useCallback((id) => {
    setGroupSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleGroupSelectAll = useCallback(() => {
    const allIds = paginatedGroups.map((g) => g.area_group_id);
    const allSelected = allIds.every((id) => groupSelectedIds.includes(id));
    if (allSelected) {
      setGroupSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setGroupSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [paginatedGroups, groupSelectedIds]);

  // Group drawer handlers
  const openAddGroup = useCallback(() => {
    if (!canManageGroups) {
      showToast('error', 'You do not have permission to add area groups.');
      return;
    }
    setEditingGroup(null);
    setGroupDrawerOpen(true);
  }, [canManageGroups, showToast]);

  // Open is allowed for all roles — drawer itself gates name/description editing.
  const openEditGroup = useCallback((group) => {
    setEditingGroup(group);
    setGroupDrawerOpen(true);
  }, []);

  const handleSaveGroup = useCallback(
    async (formData) => {
      const session = getSessionParams();
      let result;

      if (formData.area_group_id) {
        // Edit mode
        const original = areaGroupsData.find((g) => g.area_group_id === formData.area_group_id);
        const oldData = {
          ...original,
          area_ids: (original?.areas || []).map((a) => a.area_id),
        };
        result = await editAreaGroup(formData, oldData, session);
      } else {
        result = await addAreaGroup(formData, session);
      }

      if (result[0]) {
        setGroupDrawerOpen(false);
        loadAreaGroups();
        loadAreas(); // Areas display group names
        showToast('success', formData.area_group_id
          ? `${formData.area_group_name} updated`
          : `${formData.area_group_name} created`
        );
      } else {
        showToast('error', result[1] || 'Failed to save group');
      }
    },
    [areaGroupsData, getSessionParams, loadAreaGroups, loadAreas, showToast]
  );

  const handleDeleteGroup = useCallback(
    (group) => {
      if (!group) return;
      if (!canManageGroups) {
        showToast('error', 'You do not have permission to delete area groups.');
        return;
      }
      setDeleteGroupRow(group);
    },
    [canManageGroups, showToast]
  );

  const handleConfirmDeleteGroup = useCallback(async () => {
    const group = deleteGroupRow;
    if (!group) return;
    setDeleteGroupSaving(true);
    const session = getSessionParams();
    const groupName = group.area_group_name;
    const result = await deleteAreaGroups([group.area_group_id], session);
    setDeleteGroupSaving(false);
    if (result[0]) {
      setDeleteGroupRow(null);
      setGroupSelectedIds((prev) => prev.filter((id) => id !== group.area_group_id));
      setGroupDrawerOpen(false);
      loadAreaGroups();
      loadAreas();
      showToast('success', `${groupName} deleted`);
    } else {
      showToast('error', result[1] || 'Delete failed');
    }
  }, [deleteGroupRow, getSessionParams, loadAreaGroups, loadAreas, showToast]);

  const handleGroupBulkDelete = useCallback(() => {
    if (!canManageGroups) {
      showToast('error', 'You do not have permission to delete area groups.');
      return;
    }
    if (groupSelectedIds.length === 0) return;
    setGroupBulkDeleteOpen(true);
  }, [canManageGroups, groupSelectedIds.length, showToast]);

  const handleConfirmGroupBulkDelete = useCallback(async () => {
    const count = groupSelectedIds.length;
    if (count === 0) return;
    setGroupBulkDeleteSaving(true);
    const session = getSessionParams();
    const result = await deleteAreaGroups(groupSelectedIds, session);
    setGroupBulkDeleteSaving(false);
    if (result[0]) {
      setGroupBulkDeleteOpen(false);
      setGroupSelectedIds([]);
      loadAreaGroups();
      loadAreas();
      showToast('success', `${count} group${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [groupSelectedIds, getSessionParams, loadAreaGroups, loadAreas, showToast]);

  // Group exports
  const handleGroupExportAll = useCallback(() => {
    if (filteredGroups.length) exportAreaGroupsCsv(filteredGroups, 'area-groups.csv', myRoleWeight);
  }, [filteredGroups, myRoleWeight]);

  const handleGroupBulkExport = useCallback(() => {
    const selected = enrichedGroups.filter((g) => groupSelectedIds.includes(g.area_group_id));
    if (selected.length) exportAreaGroupsCsv(selected, 'area-groups-selected.csv', myRoleWeight);
  }, [groupSelectedIds, enrichedGroups, myRoleWeight]);

  // ==========================================================================
  // BUILDINGS TAB — Filter / Search / Sort pipeline
  // ==========================================================================
  /* Area counts for the building delete confirmations. Stated as a plain fact —
     how many areas sit in the building today — rather than a claim about what
     happens to them, because sp_deleteBuilding's handling of contained areas is
     not visible from this repo. Distinct for the bulk case. */
  const deleteBuildingAreaCount = useMemo(() => {
    if (!deleteBuildingRow) return 0;
    const fresh = (buildingsData || []).find(
      (b) => b.building_id === deleteBuildingRow.building_id
    );
    return ((fresh || deleteBuildingRow).areas || []).length;
  }, [deleteBuildingRow, buildingsData]);

  const buildingBulkAreaCount = useMemo(() => {
    if (!buildingBulkDeleteOpen) return 0;
    const ids = new Set();
    (buildingsData || []).forEach((b) => {
      if (!buildingSelectedIds.includes(b.building_id)) return;
      (b.areas || []).forEach((a) => ids.add(a.area_id));
    });
    return ids.size;
  }, [buildingBulkDeleteOpen, buildingSelectedIds, buildingsData]);

  const enrichedBuildings = useMemo(() => {
    return (buildingsData || []).map((b) => ({
      ...b,
      area_count: (b.areas || []).length,
    }));
  }, [buildingsData]);

  const filteredBuildings = useMemo(() => {
    let data = [...enrichedBuildings];

    // 1. Search
    if (buildingSearch.trim()) {
      const s = buildingSearch.toLowerCase();
      data = data.filter(
        (b) =>
          b.building_name?.toLowerCase().includes(s) ||
          b.street_address?.toLowerCase().includes(s) ||
          b.city?.toLowerCase().includes(s) ||
          b.state?.toLowerCase().includes(s)
      );
    }

    // 2. Column filters
    Object.entries(buildingFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      data = data.filter((row) => {
        const val = row[columnKey];
        const strVal = val != null && val !== '' ? String(val) : '(empty)';
        return values.includes(strVal);
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal, bVal;
      if (buildingSortColumn === 'area_count') {
        aVal = (a.areas || []).length;
        bVal = (b.areas || []).length;
        const result = aVal - bVal;
        return buildingSortOrder === 'asc' ? result : -result;
      }
      aVal = a[buildingSortColumn];
      bVal = b[buildingSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return buildingSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [enrichedBuildings, buildingSearch, buildingFilters, buildingSortColumn, buildingSortOrder]);

  // Buildings Pagination
  const buildingTotalFiltered = filteredBuildings.length;
  const buildingTotalPages = Math.max(1, Math.ceil(buildingTotalFiltered / buildingPerPage));
  const buildingSafePage = Math.min(buildingPage, buildingTotalPages);

  const paginatedBuildings = useMemo(() => {
    const start = (buildingSafePage - 1) * buildingPerPage;
    return filteredBuildings.slice(start, start + buildingPerPage);
  }, [filteredBuildings, buildingSafePage, buildingPerPage]);

  const buildingShowingStart = buildingTotalFiltered === 0 ? 0 : (buildingSafePage - 1) * buildingPerPage + 1;
  const buildingShowingEnd = Math.min(buildingSafePage * buildingPerPage, buildingTotalFiltered);

  useEffect(() => { setBuildingPage(1); }, [buildingSearch, buildingFilters, buildingPerPage]);

  const buildingPageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, buildingSafePage - Math.floor(maxButtons / 2));
    let end = Math.min(buildingTotalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [buildingSafePage, buildingTotalPages]);

  // Building handlers
  const handleBuildingSort = useCallback((column) => {
    if (buildingSortColumn === column) {
      setBuildingSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setBuildingSortColumn(column);
      setBuildingSortOrder('asc');
    }
  }, [buildingSortColumn]);

  const handleBuildingFilterChange = useCallback((columnKey, values) => {
    setBuildingFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleBuildingRemoveFilter = useCallback((columnKey, value) => {
    setBuildingFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleBuildingClearAllFilters = useCallback(() => {
    setBuildingFilters({});
    setBuildingSearch('');
  }, []);

  const handleBuildingSelectRow = useCallback((id) => {
    setBuildingSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleBuildingSelectAll = useCallback(() => {
    const allIds = paginatedBuildings.map((b) => b.building_id);
    const allSelected = allIds.every((id) => buildingSelectedIds.includes(id));
    if (allSelected) {
      setBuildingSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setBuildingSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [paginatedBuildings, buildingSelectedIds]);

  // Building drawer handlers
  const openAddBuilding = useCallback(() => {
    if (!canModifyBuildings) {
      showToast('error', 'You do not have permission to add buildings.');
      return;
    }
    setEditingBuilding(null);
    setBuildingDrawerOpen(true);
  }, [canModifyBuildings, showToast]);

  const openEditBuilding = useCallback((building) => {
    if (!canModifyBuildings) {
      showToast('error', 'You do not have permission to edit buildings.');
      return;
    }
    setEditingBuilding(building);
    setBuildingDrawerOpen(true);
  }, [canModifyBuildings, showToast]);

  const handleSaveBuilding = useCallback(
    async (formData) => {
      const session = getSessionParams();
      let result;

      if (formData.building_id) {
        const original = buildingsData.find((b) => b.building_id === formData.building_id);
        const oldData = {
          ...original,
          area_ids: (original?.areas || []).map((a) => a.area_id),
        };
        result = await editBuilding(formData, oldData, session);
      } else {
        result = await addBuilding(formData, session);
      }

      if (result[0]) {
        setBuildingDrawerOpen(false);
        loadBuildings();
        loadAreas(); // Areas display building name
        showToast('success', formData.building_id
          ? `${formData.building_name} updated`
          : `${formData.building_name} added`
        );
      } else {
        showToast('error', result[1] || 'Failed to save building');
      }
    },
    [buildingsData, getSessionParams, loadBuildings, loadAreas, showToast]
  );

  const handleDeleteBuilding = useCallback(
    (building) => {
      if (!building) return;
      if (!canModifyBuildings) {
        showToast('error', 'You do not have permission to delete buildings.');
        return;
      }
      setDeleteBuildingRow(building);
    },
    [canModifyBuildings, showToast]
  );

  const handleConfirmDeleteBuilding = useCallback(async () => {
    const building = deleteBuildingRow;
    if (!building) return;
    setDeleteBuildingSaving(true);
    const session = getSessionParams();
    const buildingName = building.building_name;
    const result = await deleteBuildings([building.building_id], session);
    setDeleteBuildingSaving(false);
    if (result[0]) {
      setDeleteBuildingRow(null);
      setBuildingSelectedIds((prev) => prev.filter((id) => id !== building.building_id));
      setBuildingDrawerOpen(false);
      loadBuildings();
      loadAreas();
      showToast('success', `${buildingName} deleted`);
    } else {
      showToast('error', result[1] || 'Delete failed');
    }
  }, [deleteBuildingRow, getSessionParams, loadBuildings, loadAreas, showToast]);

  const handleBuildingBulkDelete = useCallback(() => {
    if (!canModifyBuildings) {
      showToast('error', 'You do not have permission to delete buildings.');
      return;
    }
    if (buildingSelectedIds.length === 0) return;
    setBuildingBulkDeleteOpen(true);
  }, [canModifyBuildings, buildingSelectedIds.length, showToast]);

  const handleConfirmBuildingBulkDelete = useCallback(async () => {
    const count = buildingSelectedIds.length;
    if (count === 0) return;
    setBuildingBulkDeleteSaving(true);
    const session = getSessionParams();
    const result = await deleteBuildings(buildingSelectedIds, session);
    setBuildingBulkDeleteSaving(false);
    if (result[0]) {
      setBuildingBulkDeleteOpen(false);
      setBuildingSelectedIds([]);
      loadBuildings();
      loadAreas();
      showToast('success', `${count} building${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [buildingSelectedIds, getSessionParams, loadBuildings, loadAreas, showToast]);

  // Building exports
  const handleBuildingExportAll = useCallback(() => {
    if (filteredBuildings.length) exportBuildingsCsv(filteredBuildings, 'buildings.csv', myRoleWeight);
  }, [filteredBuildings, myRoleWeight]);

  const handleBuildingBulkExport = useCallback(() => {
    const selected = enrichedBuildings.filter((b) => buildingSelectedIds.includes(b.building_id));
    if (selected.length) exportBuildingsCsv(selected, 'buildings-selected.csv', myRoleWeight);
  }, [buildingSelectedIds, enrichedBuildings, myRoleWeight]);

  // ==========================================================================
  // COLUMN LABEL MAPS (for ActiveFilterBar)
  // ==========================================================================
  const areaColumnLabels = useMemo(
    () => Object.fromEntries(COLUMN_DEFS.map((c) => [c.key, c.label])),
    []
  );
  const groupColumnLabels = useMemo(
    () => Object.fromEntries(GROUP_COLUMN_DEFS.map((c) => [c.key, c.label])),
    []
  );
  const buildingColumnLabels = useMemo(
    () => Object.fromEntries(BUILDING_COLUMN_DEFS.map((c) => [c.key, c.label])),
    []
  );

  // Campus name — derive from campusList (which correctly maps session.campusData names)
  const campusName = useMemo(() => {
    const match = campusList.find((c) => c.campus_id === campusId);
    return match?.campus_name || '';
  }, [campusId, campusList]);

  // ==========================================================================
  // Helper: Pagination component (reused across 3 tabs)
  // ==========================================================================
  const renderPagination = (prefix, showingStart, showingEnd, totalFiltered, entityLabel, safePage, totalPages, pageNumbers, setPageFn, perPageVal, setPerPageFn, perPageId) => (
    <div className="ap-pagination">
      <p className="ap-pagination__info">
        Showing <span className="ap-pagination__emphasis">{showingStart}</span>&ndash;<span className="ap-pagination__emphasis">{showingEnd}</span> of{' '}
        <span className="ap-pagination__emphasis">{totalFiltered}</span> {entityLabel}
      </p>
      <div className="ap-pagination__controls">
        <div className="ap-per-page">
          <label htmlFor={perPageId} className="ap-per-page__label">Rows per page:</label>
          <select
            id={perPageId}
            value={perPageVal}
            onChange={(e) => setPerPageFn(parseInt(e.target.value, 10))}
            className="ap-per-page__select"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={300}>300</option>
          </select>
        </div>
        <nav className="ap-page-nav" aria-label={`${entityLabel} pagination`}>
          <button
            type="button"
            onClick={() => setPageFn((p) => Math.max(1, p - 1))}
            disabled={safePage <= 1}
            className="ap-page-btn"
            aria-label="Previous page"
          >
            <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          {pageNumbers.map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => setPageFn(num)}
              className={`ap-page-num ${num === safePage ? 'is-active' : ''}`}
              aria-label={`Page ${num}`}
              aria-current={num === safePage ? 'page' : undefined}
            >
              {num}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPageFn((p) => Math.min(totalPages, p + 1))}
            disabled={safePage >= totalPages}
            className="ap-page-btn"
            aria-label="Next page"
          >
            <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </nav>
      </div>
    </div>
  );

  // ==========================================================================
  // Helper: Bulk action bar (reused across 3 tabs)
  // ==========================================================================
  // `extras` carries tab-specific bulk actions ({ onEditGroups, onEditType });
  // only the Areas tab passes them today.
  const renderBulkBar = (selectedCount, entityLabel, onExport, onDelete, onClear, canDelete = true, extras = {}) => {
    const { onEditGroups = null, onEditType = null } = extras || {};
    if (selectedCount === 0) return null;
    return (
      <div className="ap-bulk-bar">
        <div className="ap-bulk-bar__left">
          <span className="ap-bulk-bar__count">
            {selectedCount} {entityLabel}{selectedCount !== 1 ? 's' : ''} selected
          </span>
          <button type="button" onClick={onClear} className="ap-bulk-bar__deselect">
            Deselect All
          </button>
        </div>
        <div className="ap-bulk-bar__right">
          <button type="button" onClick={onExport} className="ap-bulk-btn" aria-label={`Export selected ${entityLabel}s`}>
            <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
          {onEditGroups ? (
            <button type="button" onClick={onEditGroups} className="ap-bulk-btn" aria-label={`Edit groups for selected ${entityLabel}s`}>
              <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
              </svg>
              Edit Groups
            </button>
          ) : null}
          {onEditType ? (
            <button type="button" onClick={onEditType} className="ap-bulk-btn" aria-label={`Edit type for selected ${entityLabel}s`}>
              <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.878.699 2.577 0l4.318-4.318a1.823 1.823 0 000-2.577L11.16 3.66A2.25 2.25 0 009.568 3z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
              </svg>
              Edit Type
            </button>
          ) : null}
          {canDelete ? (
            <button type="button" onClick={onDelete} className="ap-bulk-btn ap-bulk-btn--danger" aria-label={`Delete selected ${entityLabel}s`}>
              <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Delete
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <>
    <NavigationV2 />
    <div className="nav2-main">
    <div className="ap-page">
      {/* Page-level toast notification */}
      {toast ? (
        <div
          className={`ap-toast ${toast.type === 'success' ? 'ap-toast--success' : 'ap-toast--error'}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === 'success' ? (
            <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          )}
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => { clearTimeout(toastTimerRef.current); setToast(null); }}
            className="ap-toast__close"
            aria-label="Dismiss"
          >
            <svg className="ap-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : null}

      {/* ============================================================
          PAGE HEADER
          ============================================================ */}
      <div className="ap-page-header">
        <div className="ap-page-header__inner">
          <div className="ap-breadcrumb-row">
            <div className="ap-breadcrumb">
              <span className="ap-breadcrumb__text">Community</span>
              <svg className="ap-icon-md ap-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="ap-breadcrumb__current">Areas</span>
            </div>
            <div className="ap-campus-row">
              <label htmlFor="ap-campus-selector" className="ap-campus-label">Campus:</label>
              {campusList.length > 1 ? (
                <select
                  id="ap-campus-selector"
                  value={campusId}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setCampusId(val);
                    updateSessionCampus(val);
                  }}
                  className="ap-campus-select"
                >
                  {campusList.map((c) => (
                    <option key={c.campus_id || c.CampusId} value={c.campus_id || c.CampusId}>
                      {c.campus_name || c.CampusName}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="ap-campus-name">{campusName}</span>
              )}
            </div>
          </div>
          <h1 className="ap-page-title">Areas</h1>
        </div>
      </div>

      {/* ============================================================
          TAB BAR
          ============================================================ */}
      <div className="ap-tab-bar">
        <div className="ap-tab-bar__inner">
          <nav className="ap-tab-nav" aria-label="Tabs">
            <Link to="/community/areas" className={`ap-tab ${activeTab === 'areas' ? 'is-active' : ''}`}>
              Areas ({areasData.length})
              {activeTab === 'areas' ? <span className="ap-tab__indicator" /> : null}
            </Link>
            <Link to="/community/areas/groups" className={`ap-tab ${activeTab === 'areagroups' ? 'is-active' : ''}`}>
              Area Groups ({enrichedGroups.length})
              {activeTab === 'areagroups' ? <span className="ap-tab__indicator" /> : null}
            </Link>
            <Link to="/community/areas/buildings" className={`ap-tab ${activeTab === 'buildings' ? 'is-active' : ''}`}>
              Buildings ({enrichedBuildings.length})
              {activeTab === 'buildings' ? <span className="ap-tab__indicator" /> : null}
            </Link>
          </nav>
        </div>
      </div>

      {/* ============================================================
          MAIN CONTENT
          ============================================================ */}
      <div className="ap-content">
        {/* ========== AREAS TAB ========== */}
        {activeTab === 'areas' ? (
          <>
            <div className="ap-controls">
              <div className="ap-controls__left">
                <div className="ap-search">
                  <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search areas..."
                    value={areaSearch}
                    onChange={(e) => setAreaSearch(e.target.value)}
                    className="ap-search__input"
                    aria-label="Search areas"
                  />
                </div>
              </div>
              <div className="ap-controls__right">
                <ColumnVisibilityToggle columns={COLUMN_DEFS} visibleColumns={areaVisibleColumns} onToggle={handleAreaColumnToggle} onReset={resetAreaColumns} />
                <button type="button" className="ap-btn ap-btn--secondary" onClick={handleAreaExportAll} aria-label="Export areas">
                  <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                {canModifyAreas ? (
                  <button
                    type="button"
                    onClick={openAddArea}
                    className="ap-btn ap-btn--primary"
                    aria-label="Add new area"
                  >
                    <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    New Area
                  </button>
                ) : null}
              </div>
            </div>
            <ActiveFilterBar filters={areaFilters} columnLabels={areaColumnLabels} onRemoveFilter={handleAreaRemoveFilter} onClearAll={handleAreaClearAllFilters} />
            {renderBulkBar(areaSelectedIds.length, 'area', handleAreaBulkExport, handleBulkDeleteAreas, () => setAreaSelectedIds([]), canModifyAreas, {
              onEditGroups: canModifyAreas ? () => setBulkAreaGroupDrawerOpen(true) : null,
              onEditType: canModifyAreas ? () => setBulkAreaTypeDrawerOpen(true) : null,
            })}
            {areasLoading ? (
              <TableSkeleton />
            ) : (
              <AreasTable
                data={paginatedAreas}
                allData={areasData}
                visibleColumns={areaVisibleColumns}
                sortColumn={areaSortColumn}
                sortOrder={areaSortOrder}
                onSort={handleAreaSort}
                filters={areaFilters}
                onFilterChange={handleAreaFilterChange}
                selectedIds={areaSelectedIds}
                onSelectRow={handleAreaSelectRow}
                onSelectAll={handleAreaSelectAll}
                onEditArea={openEditArea}
                onManageDevices={openDeviceDrawer}
                onDeleteArea={handleDeleteArea}
                onAddArea={openAddArea}
                onClearFilters={handleAreaClearAllFilters}
                canModifyAreas={canModifyAreas}
                getDeleteBlockedReason={getDeleteAreaBlockedReason}
                isGrandstream={isGrandstream}
                roomCallingAreaIds={roomCallingAreaIds}
                fallProtectionAreaIds={fallProtectionAreaIds}
              />
            )}
            {!areasLoading && areaTotalFiltered > 0
              ? renderPagination('area', areaShowingStart, areaShowingEnd, areaTotalFiltered, 'areas', areaSafePage, areaTotalPages, areaPageNumbers, setAreaPage, areaPerPage, setAreaPerPage, 'area-per-page')
              : null}
          </>
        ) : activeTab === 'areagroups' ? (
          /* ========== AREA GROUPS TAB ========== */
          <>
            <div className="ap-controls">
              <div className="ap-controls__left">
                <div className="ap-search">
                  <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search groups..."
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    className="ap-search__input"
                    aria-label="Search area groups"
                  />
                </div>
              </div>
              <div className="ap-controls__right">
                <ColumnVisibilityToggle columns={GROUP_COLUMN_DEFS} visibleColumns={groupVisibleColumns} onToggle={handleGroupColumnToggle} onReset={resetGroupColumns} />
                <button type="button" className="ap-btn ap-btn--secondary" onClick={handleGroupExportAll} aria-label="Export groups">
                  <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                {canManageGroups ? (
                  <button
                    type="button"
                    onClick={openAddGroup}
                    className="ap-btn ap-btn--primary"
                    aria-label="Add new group"
                  >
                    <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    New Group
                  </button>
                ) : null}
              </div>
            </div>
            <ActiveFilterBar filters={groupFilters} columnLabels={groupColumnLabels} onRemoveFilter={handleGroupRemoveFilter} onClearAll={handleGroupClearAllFilters} />
            {renderBulkBar(groupSelectedIds.length, 'group', handleGroupBulkExport, handleGroupBulkDelete, () => setGroupSelectedIds([]), canManageGroups)}
            {groupsLoading ? (
              <TableSkeleton />
            ) : (
              <AreaGroupsTable
                data={paginatedGroups}
                allData={enrichedGroups}
                visibleColumns={groupVisibleColumns}
                sortColumn={groupSortColumn}
                sortOrder={groupSortOrder}
                onSort={handleGroupSort}
                filters={groupFilters}
                onFilterChange={handleGroupFilterChange}
                selectedIds={groupSelectedIds}
                onSelectRow={handleGroupSelectRow}
                onSelectAll={handleGroupSelectAll}
                onEditGroup={openEditGroup}
                onDeleteGroup={handleDeleteGroup}
                onAddGroup={openAddGroup}
                onClearFilters={handleGroupClearAllFilters}
                canManageGroups={canManageGroups}
              />
            )}
            {!groupsLoading && groupTotalFiltered > 0
              ? renderPagination('group', groupShowingStart, groupShowingEnd, groupTotalFiltered, 'groups', groupSafePage, groupTotalPages, groupPageNumbers, setGroupPage, groupPerPage, setGroupPerPage, 'group-per-page')
              : null}
          </>
        ) : (
          /* ========== BUILDINGS TAB ========== */
          <>
            <div className="ap-controls">
              <div className="ap-controls__left">
                <div className="ap-search">
                  <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search buildings..."
                    value={buildingSearch}
                    onChange={(e) => setBuildingSearch(e.target.value)}
                    className="ap-search__input"
                    aria-label="Search buildings"
                  />
                </div>
              </div>
              <div className="ap-controls__right">
                <ColumnVisibilityToggle columns={BUILDING_COLUMN_DEFS} visibleColumns={buildingVisibleColumns} onToggle={handleBuildingColumnToggle} onReset={resetBuildingColumns} />
                <button type="button" className="ap-btn ap-btn--secondary" onClick={handleBuildingExportAll} aria-label="Export buildings">
                  <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                {canModifyBuildings ? (
                  <button type="button" onClick={openAddBuilding} className="ap-btn ap-btn--primary" aria-label="Add new building">
                    <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    New Building
                  </button>
                ) : null}
              </div>
            </div>
            <ActiveFilterBar filters={buildingFilters} columnLabels={buildingColumnLabels} onRemoveFilter={handleBuildingRemoveFilter} onClearAll={handleBuildingClearAllFilters} />
            {renderBulkBar(buildingSelectedIds.length, 'building', handleBuildingBulkExport, handleBuildingBulkDelete, () => setBuildingSelectedIds([]), canModifyBuildings)}
            {buildingsLoading ? (
              <TableSkeleton />
            ) : (
              <BuildingsTable
                data={paginatedBuildings}
                allData={enrichedBuildings}
                visibleColumns={buildingVisibleColumns}
                sortColumn={buildingSortColumn}
                sortOrder={buildingSortOrder}
                onSort={handleBuildingSort}
                filters={buildingFilters}
                onFilterChange={handleBuildingFilterChange}
                selectedIds={buildingSelectedIds}
                onSelectRow={handleBuildingSelectRow}
                onSelectAll={handleBuildingSelectAll}
                onEditBuilding={openEditBuilding}
                onDeleteBuilding={handleDeleteBuilding}
                onAddBuilding={openAddBuilding}
                onClearFilters={handleBuildingClearAllFilters}
                canModifyBuildings={canModifyBuildings}
              />
            )}
            {!buildingsLoading && buildingTotalFiltered > 0
              ? renderPagination('building', buildingShowingStart, buildingShowingEnd, buildingTotalFiltered, 'buildings', buildingSafePage, buildingTotalPages, buildingPageNumbers, setBuildingPage, buildingPerPage, setBuildingPerPage, 'building-per-page')
              : null}
          </>
        )}
      </div>

      {/* ============================================================
          DESTRUCTIVE CONFIRMATIONS
          ============================================================
          All four require typing "confirm". The two single-entity dialogs are
          `elevated` because they can be opened from inside a drawer, which sits
          above the default dialog backdrop. */}
      {deleteAreaRow ? (
        <ConfirmTypedDialog
          title="Delete Area"
          confirmLabel="Delete"
          savingLabel="Deleting…"
          elevated
          onClose={() => setDeleteAreaRow(null)}
          onConfirm={handleConfirmDeleteArea}
          saving={deleteAreaSaving}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to delete <strong>{deleteAreaRow.area_name}</strong>?
          </p>
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            The area will no longer appear anywhere in the system
            {deleteAreaRowGroupCount > 0 ? (
              <>
                {' '}and will be removed from{' '}
                <strong>
                  {deleteAreaRowGroupCount} area group{deleteAreaRowGroupCount > 1 ? 's' : ''}
                </strong>
              </>
            ) : null}
            . <strong>This cannot be undone.</strong>
          </p>
        </ConfirmTypedDialog>
      ) : null}

      {bulkDeleteAreasOpen ? (
        <ConfirmTypedDialog
          title={`Delete ${bulkAreaDeletePlan.deletable.length} Area${bulkAreaDeletePlan.deletable.length > 1 ? 's' : ''}`}
          confirmLabel="Delete"
          savingLabel="Deleting…"
          onClose={() => setBulkDeleteAreasOpen(false)}
          onConfirm={handleConfirmBulkDeleteAreas}
          saving={bulkDeleteAreasSaving}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to delete{' '}
            <strong>
              {bulkAreaDeletePlan.deletable.length} area
              {bulkAreaDeletePlan.deletable.length > 1 ? 's' : ''}
            </strong>
            ?
          </p>
          {bulkAreaDeletePlan.blocked > 0 ? (
            <p style={{ margin: 'var(--space-2) 0 0', lineHeight: 1.6 }}>
              <strong>
                {bulkAreaDeletePlan.blocked} of the {bulkAreaDeletePlan.selected.length} selected
              </strong>{' '}
              will be skipped because they still have residents or devices assigned.
            </p>
          ) : null}
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            {bulkAreaDeletePlan.deletable.length > 1 ? 'These areas' : 'This area'} will no longer appear anywhere in
            the system. <strong>This cannot be undone.</strong>
          </p>
        </ConfirmTypedDialog>
      ) : null}

      {deleteGroupRow ? (
        <ConfirmTypedDialog
          title="Delete Area Group"
          confirmLabel="Delete"
          savingLabel="Deleting…"
          elevated
          onClose={() => setDeleteGroupRow(null)}
          onConfirm={handleConfirmDeleteGroup}
          saving={deleteGroupSaving}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to delete <strong>{deleteGroupRow.area_group_name}</strong>?
          </p>
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            {deleteGroupAreaCount > 0 ? (
              <>
                This group will be removed from the{' '}
                <strong>
                  {deleteGroupAreaCount} area{deleteGroupAreaCount > 1 ? 's' : ''}
                </strong>{' '}
                assigned to it.{' '}
              </>
            ) : null}
            The areas themselves are not deleted. <strong>This cannot be undone.</strong>
          </p>
        </ConfirmTypedDialog>
      ) : null}

      {groupBulkDeleteOpen ? (
        <ConfirmTypedDialog
          title={`Delete ${groupSelectedIds.length} Area Group${groupSelectedIds.length > 1 ? 's' : ''}`}
          confirmLabel="Delete"
          savingLabel="Deleting…"
          onClose={() => setGroupBulkDeleteOpen(false)}
          onConfirm={handleConfirmGroupBulkDelete}
          saving={groupBulkDeleteSaving}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to delete{' '}
            <strong>
              {groupSelectedIds.length} area group{groupSelectedIds.length > 1 ? 's' : ''}
            </strong>
            ?
          </p>
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            {groupBulkAreaCount > 0 ? (
              <>
                {groupSelectedIds.length > 1 ? 'These groups' : 'This group'} will be removed from{' '}
                <strong>
                  {groupBulkAreaCount} area{groupBulkAreaCount > 1 ? 's' : ''}
                </strong>
                .{' '}
              </>
            ) : null}
            The areas themselves are not deleted. <strong>This cannot be undone.</strong>
          </p>
        </ConfirmTypedDialog>
      ) : null}

      {deleteBuildingRow ? (
        <ConfirmTypedDialog
          title="Delete Building"
          confirmLabel="Delete"
          savingLabel="Deleting…"
          elevated
          onClose={() => setDeleteBuildingRow(null)}
          onConfirm={handleConfirmDeleteBuilding}
          saving={deleteBuildingSaving}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to delete <strong>{deleteBuildingRow.building_name}</strong>?
          </p>
          {deleteBuildingAreaCount > 0 ? (
            <p style={{ margin: 'var(--space-2) 0 0', lineHeight: 1.6 }}>
              <strong>
                {deleteBuildingAreaCount} area{deleteBuildingAreaCount > 1 ? 's' : ''}
              </strong>{' '}
              {deleteBuildingAreaCount > 1 ? 'are' : 'is'} currently assigned to this building.
            </p>
          ) : null}
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            The building will no longer appear anywhere in the system.{' '}
            <strong>This cannot be undone.</strong>
          </p>
        </ConfirmTypedDialog>
      ) : null}

      {buildingBulkDeleteOpen ? (
        <ConfirmTypedDialog
          title={`Delete ${buildingSelectedIds.length} Building${buildingSelectedIds.length > 1 ? 's' : ''}`}
          confirmLabel="Delete"
          savingLabel="Deleting…"
          onClose={() => setBuildingBulkDeleteOpen(false)}
          onConfirm={handleConfirmBuildingBulkDelete}
          saving={buildingBulkDeleteSaving}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to delete{' '}
            <strong>
              {buildingSelectedIds.length} building{buildingSelectedIds.length > 1 ? 's' : ''}
            </strong>
            ?
          </p>
          {buildingBulkAreaCount > 0 ? (
            <p style={{ margin: 'var(--space-2) 0 0', lineHeight: 1.6 }}>
              <strong>
                {buildingBulkAreaCount} area{buildingBulkAreaCount > 1 ? 's' : ''}
              </strong>{' '}
              {buildingBulkAreaCount > 1 ? 'are' : 'is'} currently assigned to{' '}
              {buildingSelectedIds.length > 1 ? 'these buildings' : 'this building'}.
            </p>
          ) : null}
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            {buildingSelectedIds.length > 1 ? 'These buildings' : 'This building'} will no longer appear anywhere in
            the system. <strong>This cannot be undone.</strong>
          </p>
        </ConfirmTypedDialog>
      ) : null}

      {/* ============================================================
          DRAWERS
          ============================================================ */}
      {/* Bulk area-type edit drawer */}
      <BulkAreaTypeDrawer
        isOpen={bulkAreaTypeDrawerOpen}
        onClose={() => setBulkAreaTypeDrawerOpen(false)}
        onSave={handleBulkEditAreaTypes}
        areaTypes={areaTypes}
        areas={areasData.filter((a) => areaSelectedIds.includes(a.area_id))}
        saving={bulkAreaTypeSaving}
      />

      {/* Bulk area-group edit drawer */}
      <BulkAreaGroupDrawer
        isOpen={bulkAreaGroupDrawerOpen}
        onClose={() => setBulkAreaGroupDrawerOpen(false)}
        onSave={handleBulkEditAreaGroups}
        areaGroups={areaGroupsData}
        areas={areasData.filter((a) => areaSelectedIds.includes(a.area_id))}
        saving={bulkAreaGroupSaving}
      />

      <AreaDrawer
        isOpen={areaDrawerOpen}
        onClose={() => setAreaDrawerOpen(false)}
        area={editingArea}
        onSave={handleSaveArea}
        onDelete={handleDeleteArea}
        areaTypes={areaTypes}
        buildings={buildingsData}
        areaGroups={areaGroupsData}
        canModifyAreas={canModifyAreas}
        deleteBlockedReason={editingArea ? getDeleteAreaBlockedReason(editingArea) : null}
      />

      <AreaDeviceDrawer
        isOpen={deviceDrawerOpen}
        onClose={() => setDeviceDrawerOpen(false)}
        area={deviceArea}
        assignedDevices={deviceDrawerAssigned}
        availableDevices={devicesData}
        onAssignDevice={handleAssignDeviceToArea}
        onRemoveDevice={handleRemoveDeviceFromArea}
        canManageRestrictedDevices={canManageRestrictedDevices}
        isRestrictedDevice={isRestrictedDevice}
        onAddNewDevice={() => setEasyAddOpen(true)}
      />

      {/* Simplified add-and-assign flow for brand-new devices */}
      <EasyAddDeviceDrawer
        isOpen={easyAddOpen}
        onClose={() => setEasyAddOpen(false)}
        mode="area"
        classPrefix="ap"
        roleWeight={myRoleWeight}
        target={deviceArea}
        existingDevices={devicesData}
        campusId={campusId}
        sessionId={getSessionId()}
        onAdd={handleEasyAddAreaDevice}
        onAssignExisting={handleEasyAssignExistingToArea}
      />

      <AreaGroupDrawer
        isOpen={groupDrawerOpen}
        onClose={() => setGroupDrawerOpen(false)}
        group={editingGroup}
        onSave={handleSaveGroup}
        onDelete={handleDeleteGroup}
        areas={areasData}
        canManageGroups={canManageGroups}
      />

      <BuildingDrawer
        isOpen={buildingDrawerOpen}
        onClose={() => setBuildingDrawerOpen(false)}
        building={editingBuilding}
        onSave={handleSaveBuilding}
        onDelete={handleDeleteBuilding}
        areas={areasData}
      />
    </div>
    </div>
    </>
  );
};

export default AreasPage;
